import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";

/**
 * Interactive drag slider that reveals the "after" image over the "before".
 * Supports mouse drag, touch, and keyboard arrows.
 */
export default function BeforeAfterSlider({ before, after, alt = "Before / after", ariaId }) {
  const [pct, setPct] = useState(50);
  const wrapperRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const raw = ((clientX - rect.left) / rect.width) * 100;
    setPct(Math.max(0, Math.min(100, raw)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    e.preventDefault();
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onUp = () => { dragging.current = false; };

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 4));
  };

  return (
    <div
      ref={wrapperRef}
      data-testid={ariaId || "before-after-slider"}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
      className="relative aspect-[4/3] overflow-hidden bg-[#143D63] select-none cursor-ew-resize group"
    >
      <img src={after} alt={`${alt} — after`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" draggable="false" />
      <div
        style={{ width: `${pct}%` }}
        className="absolute inset-y-0 left-0 overflow-hidden"
      >
        <img
          src={before}
          alt={`${alt} — before`}
          className="w-full h-full object-cover"
          style={{ width: `${100 * (100 / Math.max(pct, 0.001))}%`, maxWidth: "none" }}
          loading="lazy"
          decoding="async"
          draggable="false"
        />
      </div>

      {/* Labels */}
      <span className="absolute top-4 left-4 bg-[#8B0000] text-[#F9F8F6] text-[10px] tracking-widest font-bold px-2.5 py-1 uppercase">Before</span>
      <span className="absolute top-4 right-4 bg-[#F8C21C] text-[#143D63] text-[10px] tracking-widest font-bold px-2.5 py-1 uppercase">After</span>

      {/* Handle */}
      <div
        style={{ left: `${pct}%` }}
        className="absolute top-0 bottom-0 -translate-x-1/2 pointer-events-none flex items-center justify-center"
      >
        <div className="w-[2px] h-full bg-[#F9F8F6]/85 shadow-[0_0_8px_rgba(0,0,0,0.6)]" />
        <button
          type="button"
          tabIndex={0}
          onKeyDown={onKey}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Drag to compare"
          data-testid={`${ariaId || "before-after"}-handle`}
          className="pointer-events-auto absolute w-11 h-11 rounded-full bg-[#F9F8F6] border-2 border-[#F8C21C] shadow-lg flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#F8C21C]"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#143D63" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
            <polyline points="9 18 15 12 9 6" transform="translate(6 0)" />
          </svg>
        </button>
      </div>
    </div>
  );
}
